import { Chart, useChart } from '@chakra-ui/charts';
import { Box } from '@chakra-ui/react';
import type { ConversionChartDataPoint } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { formatChartCount, formatChartDay } from './chart-locale-format';
import { Area, AreaChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';

interface ConversionChartProps {
  data?: ConversionChartDataPoint[];
  isLoading?: boolean;
}

export const ConversionChart: React.FC<ConversionChartProps> = ({ data, isLoading = false }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  // Transform data for Chakra Charts
  const chartData =
    data?.map((point) => ({
      date: point.date,
      conversions: point.conversions,
    })) || [];

  const chart = useChart({
    data: chartData,
    series: [
      {
        name: 'conversions',
        color: 'orange.emphasized',
        label: t('dashboard.stats.conversions'),
      },
    ],
  });

  if (isLoading || !data) {
    return (
      <Box
        height="450px"
        bg="gray.50"
        borderRadius="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {t('dashboard.stats.chartLoading')}
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box
        height="400px"
        bg="gray.50"
        borderRadius="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {t('dashboard.stats.noChartData')}
      </Box>
    );
  }

  return (
    <Box width="100%">
      <Chart.Root height="400px" chart={chart}>
        <AreaChart data={chart.data}>
          {/* Gradient definitions */}
          {chart.series.map((item) => (
            <defs key={item.name}>
              <Chart.Gradient
                id={`${item.name}-gradient`}
                stops={[
                  { offset: '0%', color: item.color, opacity: 0.3 },
                  { offset: '100%', color: item.color, opacity: 0.05 },
                ]}
              />
            </defs>
          ))}

          {/* Grid */}
          <CartesianGrid stroke={chart.color('border')} vertical={false} strokeDasharray="3 3" />

          {/* X Axis */}
          <XAxis
            dataKey={chart.key('date')}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value) => formatChartDay(value, locale)}
          />

          {/* Y Axis */}
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={[0, 'auto']}
            tickFormatter={(value) => formatChartCount(value, locale)}
          />

          {/* Tooltip */}
          <Tooltip
            cursor={false}
            animationDuration={100}
            content={
              <Chart.Tooltip
                labelFormatter={(value) => formatChartDay(value, locale)}
                formatter={(value) => formatChartCount(Number(value), locale)}
              />
            }
          />

          {/* Legend */}
          <Legend content={<Chart.Legend />} />

          {/* Areas with gradients */}
          {chart.series.map((item) => (
            <Area
              key={item.name}
              type="monotone"
              isAnimationActive={false}
              dataKey={chart.key(item.name)}
              fill={`url(#${item.name}-gradient)`}
              stroke={chart.color(item.color)}
              strokeWidth={2}
              baseValue={0}
            />
          ))}
        </AreaChart>
      </Chart.Root>
    </Box>
  );
};
